import React, {
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
} from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import last from 'lodash/last';
import delay from 'lodash/delay';
import {
  useFormContext,
} from 'react-hook-form';
import IconButton from '@mui/material/IconButton';
import EditIcon from 'core/icons/edit';
import Button from 'pages/Dashboard/components/Button';
import MediaPreview from 'pages/Dashboard/components/MediaPreview';
import {
  PatientCompact,
} from 'pages/Dashboard/pages/Encounters/types/patient';
import PatientSelect from 'pages/Dashboard/pages/Todos/components/PatientSelect';
import {
  WizardContext,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/wizardContext';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import EOBPaymentInfo from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/EOBPaymentInfo';
import ToolTip from 'core/components/Tooltip';
import {
  DeleteIcon,
} from 'core/icons';
import {
  MultimediaType,
  MediaOriginatorType,
} from 'pages/Dashboard/utils/constants';
import useDeleteMediaHandler from 'pages/Dashboard/hooks/useDeleteMediaHandler';
import {
  getClaimIdsWithCPRCMissingServiceAllocation,
} from 'pages/Dashboard/pages/Billing/Payments/utils';

export default function SelectPatientStep() {
  const {
    patients,
    visibleDoc,
    addPatient,
    toggleDoc,
    currentPatient,
    onClose,
    setEOBFound,
    setInReview,
    hasEOBServiceCodePaymentInfos,
    removePatient,
    removeEOBDocument,
    handleEOBDrop,
  } = useContext(WizardContext);
  const inputRef = useRef<HTMLInputElement>(null!);
  const [patient, setPatient] = useState<PatientCompact | null>(currentPatient);
  const { watch } = useFormContext();
  const [eobDoc, eob] = watch(['eobDoc', 'eob']);
  const isNew = isNil(eob?.eobId);
  const invalidCprcClaimIds = useMemo(
    () => getClaimIdsWithCPRCMissingServiceAllocation(
      eob?.eobServiceCodePaymentInfos ?? [],
      eob?.eobClaimPayments ?? [],
    ),
    [eob?.eobServiceCodePaymentInfos, eob?.eobClaimPayments],
  );
  const canProceedToReview = hasEOBServiceCodePaymentInfos && invalidCprcClaimIds.length === 0;

  const { onMediaDelete } = useDeleteMediaHandler();

  useEffect(() => {
    delay(() => { inputRef.current?.focus?.(); }, 500);
  }, []);

  const ignorePatientsSet = useMemo(() => (
    new Set<number>(patients.map(({ patientId }) => patientId ?? 0))
  ), [patients]);

  return (
    <>
      <div className="min-h-[480px] flex flex-1 flex-row w-full justify-between gap-3 overflow-y-hidden">
        <div className={clsx('flex flex-col gap-form-row', {
          'w-3/5': visibleDoc,
          'w-full': !visibleDoc,
        })}
        >
          <EOBPaymentInfo />
          <div className="w-full flex flex-row gap-1 px-1">
            <PatientSelect
              id="patient-lookup"
              inputRef={inputRef as RefObject<HTMLInputElement>}
              label="Patients"
              placeholder="Enter patient name, phone or date of birth"
              allowDefault
              value={patient}
              ignoreIdsSet={ignorePatientsSet}
              onChange={setPatient}
            />
          </div>
          <div className="w-full flex-1 overflow-y-hidden mt-2">
            {patients.length > 0 && (
              <div className="flex flex-col gap-3 max-h-[calc(100%-24px)] w-full overflow-auto">
                  {patients.map((patient) => (
                    <div
                      key={patient.patientId}
                      className="flex !w-fit gap-2"
                    >
                      <Button
                        variant="text"
                        color="primary"
                        className="flex !w-fit gap-2"
                        onClick={() => { addPatient(patient); }}
                      >
                        <span className="!max-w-[initial] !text-gray-500">
                          <EditIcon className="!text-inherit" />
                          <span className="!text-gray-800 font-semibold">
                            {getDisplayName(patient.formattedNames)}
                            {' '}
                            (
                            {patient.formattedDateOfBirth}
                            )
                          </span>
                        </span>
                      </Button>
                      <ToolTip title="Remove Patient">
                        <IconButton
                          color="error"
                          className="!p-0 !-mt-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            removePatient(patient);
                          }}
                        >
                          <DeleteIcon className="!w-5 !h-5 !text-zinc-800" />
                        </IconButton>
                      </ToolTip>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
        {visibleDoc && (
          <div className="flex w-2/5">
            <MediaPreview
              media={eobDoc?.media!}
              className="!overflow-auto !max-w-full !max-h-[100vh-64px]"
              thumbnail
              onUpdate={handleEOBDrop}
              mediaType={MultimediaType.EOB}
              onDelete={() => {
                if (!isNil(eobDoc?.media.mediaId)) {
                  onMediaDelete({
                    list: [
                      {
                        originatorType: MediaOriginatorType.Billing,
                        mediaId: eobDoc.media.mediaId ?? 0,
                        fileName: eobDoc.media.fileName ?? '',
                      },
                    ],
                  });
                } else {
                  removeEOBDocument();
                }
              }}
            />
          </div>
        )}
      </div>
      <div className="flex justify-between items-center gap-3">
        <Button
          variant="outlined"
          type="button"
          onClick={onClose}
        >
          Close
        </Button>
        <div className="flex flex-row justify-end gap-3">
          {isNew && (
            <Button
              variant="outlined"
              type="button"
              onClick={() => { setEOBFound(false); }}
            >
              Back
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={isNil(patient)}
            onClick={() => {
              if (!isNil(patient)) {
                addPatient(patient!);
              }
            }}
          >
            Record EOB
            {!isNil(patient) && (
              <>
                {' for '}
                {getDisplayName(patient?.formattedNames)}
              </>
            )}
          </Button>
          {hasEOBServiceCodePaymentInfos && (
            <ToolTip
              title="CPRC added for a claim without service allocation"
              hide={canProceedToReview}
            >
              <div className={clsx(
                'w-full',
                { 'cursor-not-allowed': !canProceedToReview },
              )}
              >
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={!canProceedToReview || (isNil(patient) && patients.length === 0)}
                  onClick={() => {
                    addPatient(last(patients)!);
                    setInReview(true);
                    toggleDoc(false);
                  }}
                >
                  Review
                </Button>
              </div>
            </ToolTip>
          )}
        </div>
      </div>
    </>
  );
}
